import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionModal } from '../../../../DesignSystem/ActionModal';
import Button from '../../../../generic-components/Button';
import Heading from '../../../../generic-components/Heading';
import { useDeletePaymentOption } from '../../../../hooks/mutations/useDeletePamentOption';
import { useUpdatePaymentOptionMutation } from '../../../../hooks/mutations/useUpdatePaymentOptionMutation';
import { useStore } from '../../../../store';
import { OnboardingContext } from '../InvoiceOnboarding';
import { SUBMIT_TYPES } from '../consts/consts';
import { SpinnerColor } from '../../../../generic-components/Spinner';
import { reportActOnSpeedBump, reportViewSpeedBump } from '../../../../analytics/speedBump.analytics';

interface IProps {
  method: any;
  triggerReFetchOptionsList: () => void;
}

export const StripeCreditBody = ({ method, triggerReFetchOptionsList }: IProps) => {
  const {
    user: { customerId },
  } = useStore((state) => state);
  const flowContext = useContext(OnboardingContext);
  const { onStepSubmit } = flowContext;
  const [showLeaveWarning, setShowLeaveWarning] = useState<boolean>(false);
  const { t } = useTranslation();

  const { mutateAsync: updatePayment, isLoading: isLoadingUpdate } = useUpdatePaymentOptionMutation({
    customerId,
    optionType: method.type,
    link: '',
    onSuccess: (data) => {
      onStepSubmit({ type: SUBMIT_TYPES.PAYMENTS.STRIPE_REDIRECT, data: data.appUrl });
    },
  });

  const handleRedirectOutside = () => {
    reportActOnSpeedBump('Continue');
    updatePayment();
    setShowLeaveWarning(false);
  };

  const handleCancelRedirectOutside = () => {
    reportActOnSpeedBump('Back');
    setShowLeaveWarning(false);
  };

  const { mutateAsync: deletePayment, isLoading: isLoadingDelete } = useDeletePaymentOption({
    customerId,
    type: method.type,
  });

  const handleDeleteAndUpdateList = async () => {
    await deletePayment();
    triggerReFetchOptionsList();
  };

  const handleStripeContinue = () => {
    setShowLeaveWarning(true);
    reportViewSpeedBump('Invoice Payment');
  };

  return !method.activated ? (
    <div className='flex flex-col items-center px-[40px] pb-[26px]'>
      {/* eslint-disable-next-line react/no-danger */}
      <div className='text-center mb-[22px]' dangerouslySetInnerHTML={{ __html: method?.additionalInfo }} />
      <Heading title={method?.description} className='text-center font-metric text-[1rem] text-bw07 mb-[22px]' />
      <Button
        isLoading={isLoadingUpdate}
        title='Continue Registration'
        className='min-w-[266.5px] max-h-[44px] mx-auto w-fit flex items-center justify-center font-metric text-bw00 bg-action03 rounded-[4px] py-[10px] px-[55px]'
        id='stripeCreditContinue'
        onClick={handleStripeContinue}
      />
      {showLeaveWarning && (
        <ActionModal
          title={t('NAVIGATE_OUTSIDE_MODAL.TITLE')}
          description={t('NAVIGATE_OUTSIDE_MODAL.DESCRIPTION')}
          onSubmit={handleRedirectOutside}
          onCancel={handleCancelRedirectOutside}
          cancelButtonTitle={t('NAVIGATE_OUTSIDE_MODAL.CANCEL')}
          mainButtonTitle={t('NAVIGATE_OUTSIDE_MODAL.CONTINUE')}
          disclaimerText={t('NAVIGATE_OUTSIDE_MODAL.DISCLAIMER')}
        />
      )}
    </div>
  ) : (
    <div className='flex flex-col items-center pb-[10px]'>
      {/* eslint-disable-next-line react/no-danger */}
      <div className='text-center mb-[22px] px-[40px]' dangerouslySetInnerHTML={{ __html: method?.additionalInfo }} />
      <Heading
        title={method?.description}
        className='px-[40px] text-center font-metric text-[1rem] text-bw07 mb-[22px]'
      />
      <div className='flex items-center gap-[12px] mb-[20px]'>
        <Button
          isLoading={isLoadingDelete}
          title={`Delete ${method.appName} Connect`}
          className='py-[10px] px-[35px] text-red02 border border-red02 rounded-[4px] hover:bg-red01'
          id='stripeCreditContinue'
          onClick={handleDeleteAndUpdateList}
          submitButtonSpinnerColor={SpinnerColor.Red}
        />
        <Button
          isLoading={isLoadingUpdate}
          title='Edit'
          className='hover:bg-action04 hover:text-bw00 py-[10px] px-[35px] text-action03 border border-action03 rounded-[4px]'
          id='stripeCreditContinue'
          onClick={updatePayment}
          submitButtonSpinnerColor={SpinnerColor.Blue}
        />
      </div>
      <div className='flex py-[10px] border-t border-bw03 px-[40px]'>
        <Heading
          title='By tapping Continue you agree to share your personal and bank account details with Stripe, and to link your Lili account to a Stripe account. You can read the Stripe Services Agreement here'
          className='text-center font-sans text-[12px] text-bw07'
        />
      </div>
    </div>
  );
};
